import type {
  CreateContestRequestBody,
  GetConsumerLoyaltyProfileResponse,
  GetRewardExtendedResponse,
  GetRewardResponse,
} from "@jti/evo-client";
import {
  createContest,
  createEvoClient,
  getAchievements,
  getBuyPrize,
  getConsumerEligibleRewards,
  getConsumerLoyaltyProfile,
  getConsumerOrder,
  getConsumerPointsHistory,
  getConsumerRewardsCampaignsOrPrizes,
  getConsumerTier,
  getContestStatus,
  getEarningRuleByName,
  getEarningsForConsumer,
  getLoyaltyPublicDomainConfiguration,
  getQrCodeDetails,
  getQrCodeRead,
  getReward,
  getRewardExtended,
  getTiers,
  listEarnings,
  listRewardFilters,
  listRewards,
  listRewardsExtended,
  updateArticleRead,
  updateQuizCompleted,
  updateVideoWatched,
} from "@jti/evo-client";
import type { AddressStored } from "@jti/fe-common";
import { getConfig } from "@jti/fe-common";
import type {
  ArticleReadActionReturnType,
  BuyPrizeElementResponseType,
  CampaignOrderData,
  CustomerPointsHistoryResponseType,
  CustomerReferralHistoryResponseType,
  GetAchievementsResponse,
  GetArticlesPayloadType,
  GetArticlesResponseType,
  GetContestsResponseType,
  GetEligibleCampaignsResponseType,
  GetPointsResponseType,
  GetPrizesInputType,
  GetPrizesResponseType,
  GetProductPointsReturnType,
  GetQrCodeDetailsPayload,
  GetQrCodeDetailsResponseType,
  GetRewardExtendedPayload,
  GetRewardFiltersResponseType,
  GetRewardPayload,
  GetTiersResponseType,
  GetUserTierDataResponseType,
  GetUserTierResponseType,
  QuizCompleteActionReturnType,
  ScanQrActionReturnType,
  VideoWatchActionReturnType,
} from "@jti/rest-api-types";

import { calculateProductPoints, getUser } from "../../../utils";
import type { RestResponseType } from "../../Rest";
import type { LoyaltyApiContact, LoyaltySummaryResponse } from "../LoyaltyApi.types";

import { contestsFormDataBodySerializer } from "./utils/contestsFormDataBodySerializer";
import { convertCampaignOrderToEligibleRewardRequest } from "./utils/convertCampaignOrderToEligibleRewardRequest";

import {
  convertEvoGetPrizes,
  convertEvoPointsHistory,
  convertEvoRewardFilters,
  convertFormAddress,
  convertReferralHistoryEvoResponse,
  convertRewardFilterToEvoRewardType,
  getArticlesFromEvoPayload,
} from "./utils";

const createEmptyPointsResponse = (): GetPointsResponseType => ({
  points: 0,
  expire_at: "",
  pending_points: 0,
  points_to_expire: 0,
});

type SerializedTypedIdentifiers = {
  [k: string]: string[];
};

const serializeTypedIdentifiers = <T extends GetArticlesPayloadType["typedIdentifiers"]>(typedIdentifiers: T) => {
  if (!typedIdentifiers) {
    return {};
  }

  return Object.entries(typedIdentifiers).reduce<SerializedTypedIdentifiers>((acc, [type, identifiers]) => {
    acc[`typed_identifiers[${type}][]`] = identifiers;
    return acc;
  }, {});
};

export class LoyaltyApiEvo implements LoyaltyApiContact {
  private readonly client;
  private static instance: LoyaltyApiEvo;
  constructor(
    loyaltyApiUrl: string,
    protected loyaltyTenantToken: string,
    protected recaptchaId: string,
    protected isLoyaltyEnabled: boolean
  ) {
    const { ciam_token } = getUser();
    this.client = createEvoClient({
      baseUrl: loyaltyApiUrl,
      recaptchaId,
      tenantToken: loyaltyTenantToken,
      ciamToken: ciam_token,
      throwOnError: true,
    });
  }

  static init() {
    if (this.instance) {
      return this.instance;
    }
    const { EVO_DOMAIN, EVO_TENANT, "X-RECAPTCHA-ID": recaptchaId, IS_LOYALTY_ENABLED } = getConfig();
    const instance = new LoyaltyApiEvo(EVO_DOMAIN, EVO_TENANT, recaptchaId, IS_LOYALTY_ENABLED === "true");
    this.instance = instance;
    return instance;
  }

  async getArticles({ uuid, typedIdentifiers }: GetArticlesPayloadType): Promise<GetArticlesResponseType> {
    const baseOptions = {
      client: this.client,
      query: {
        ...serializeTypedIdentifiers(typedIdentifiers),
      },
    };

    const { data } = await (!uuid
      ? listEarnings(baseOptions)
      : getEarningsForConsumer({
          ...baseOptions,
          path: {
            uuid,
          },
        }));

    return getArticlesFromEvoPayload(data?.data);
  }

  async getStatus(): Promise<boolean> {
    if (!this.isLoyaltyEnabled) {
      return false;
    }
    try {
      const { data } = await getLoyaltyPublicDomainConfiguration({
        client: this.client,
      });

      return Boolean(data?.is_active);
    } catch {
      return false;
    }
  }

  async getPoints(uuid: string): Promise<GetPointsResponseType> {
    const failureGetPointsResponse = createEmptyPointsResponse();
    try {
      const { data } = await getConsumerLoyaltyProfile({
        client: this.client,
        path: {
          uuid: uuid,
        },
      });

      if (!data?.data) {
        return failureGetPointsResponse;
      }

      return data.data;
    } catch {
      return failureGetPointsResponse;
    }
  }

  async getLoyaltySummary(uuid: string, _ciam_token: string, skipCache?: boolean): Promise<LoyaltySummaryResponse> {
    const emptySummary: LoyaltySummaryResponse = {
      points: createEmptyPointsResponse(),
      tier: null,
    };

    try {
      const { data } = await getConsumerLoyaltyProfile({
        client: this.client,
        headers: {
          "X-SKIP-SW-CACHE": skipCache,
        },
        path: {
          uuid,
        },
      });

      if (!data?.data) {
        return emptySummary;
      }

      const { tier_name, tier_image_url, points, expire_at, pending_points, points_to_expire } = data.data;

      const pointsData: GetPointsResponseType = {
        points: points ?? 0,
        expire_at: expire_at ?? "",
        pending_points: pending_points ?? 0,
        points_to_expire: points_to_expire ?? 0,
      };

      const tierData = tier_name
        ? {
            name: tier_name,
            image_url: tier_image_url ?? "",
          }
        : null;

      return {
        points: pointsData,
        tier: tierData,
      };
    } catch {
      return emptySummary;
    }
  }

  async getLoyaltyProfile(uuid: string, _: string, skipCache?: boolean): Promise<GetConsumerLoyaltyProfileResponse> {
    const { data } = await getConsumerLoyaltyProfile({
      client: this.client,
      headers: {
        "X-SKIP-SW-CACHE": skipCache,
      },
      path: {
        uuid: uuid,
      },
    });

    if (!data?.data) throw new Error("No data found in getLoyaltyProfile");

    return data;
  }

  async getEligibleCampaigns(
    uuid: string,
    _ciam_token: string,
    order?: CampaignOrderData
  ): Promise<GetEligibleCampaignsResponseType[]> {
    try {
      if (order) {
        const body = convertCampaignOrderToEligibleRewardRequest(order);
        const { data } = await getConsumerEligibleRewards({
          client: this.client,
          path: { uuid },
          body,
        });

        if (!data?.data) {
          return [];
        }

        return data.data.map((item) => ({
          campaign_id: item.reward_uuid,
          points: item.points,
          price: item.price,
          is_eligible: item.is_eligible,
        }));
      }

      const { data } = await getConsumerRewardsCampaignsOrPrizes({
        client: this.client,
        path: {
          uuid,
        },
        query: {
          type: "campaign",
        },
      });

      if (!data) {
        return [];
      }

      return data.data.map((item) => ({
        campaign_id: item.identifier,
        points: item.points,
        price: item.price,
        is_eligible: item.has_coupons,
      }));
    } catch {
      return [];
    }
  }

  async getPointsHistory(
    consumer_uuid: string,
    _: string,
    perPage: number = 10,
    page: number = 1
  ): Promise<CustomerPointsHistoryResponseType> {
    const emptyGetPointsHistoryResponse: CustomerPointsHistoryResponseType = {
      items: [],
      statusCode: 400,
      total: 0,
    };
    try {
      const { data, response } = await getConsumerPointsHistory({
        client: this.client,
        path: { uuid: consumer_uuid },
        query: {
          page,
          per_page: perPage,
          type: "history",
        },
      });

      if (!data?.data) {
        return { ...emptyGetPointsHistoryResponse, statusCode: response.status };
      }

      return convertEvoPointsHistory(data, response.status);
    } catch {
      return emptyGetPointsHistoryResponse;
    }
  }

  async getTiers(): Promise<RestResponseType<GetTiersResponseType[]>> {
    const { data, response } = await getTiers({
      client: this.client,
    });

    return { data: data?.data ?? [], statusCode: response.status };
  }

  async getUserTier(consumer_uuid: string): Promise<GetUserTierResponseType> {
    const { data } = await getConsumerTier({
      client: this.client,
      path: {
        uuid: consumer_uuid,
      },
    });

    if (!data?.data) {
      throw new Error("No data found");
    }

    return {
      image_url: data.data?.image_url ?? "",
      name: data.data.name,
    };
  }

  async getUserTierData(consumer_uuid: string): Promise<RestResponseType<GetUserTierDataResponseType>> {
    const { data, response } = await getConsumerLoyaltyProfile({
      client: this.client,
      path: {
        uuid: consumer_uuid,
      },
    });

    if (!data?.data) {
      throw new Error("No data found");
    }

    return { ...data.data, statusCode: response.status } as RestResponseType<GetUserTierDataResponseType>;
  }

  async getEarnedPoints(order_id: string, _: string, uuid: string = ""): Promise<number> {
    const { data } = await getConsumerOrder({
      client: this.client,
      path: {
        order_id,
        uuid,
      },
    });

    return data?.data?.points ?? 0;
  }

  async buyPrize(
    consumer_uuid: string,
    prize_uuid: string,
    qty: number,
    _: string,
    address?: AddressStored
  ): Promise<RestResponseType<BuyPrizeElementResponseType>> {
    const { data, response } = await getBuyPrize({
      client: this.client,
      path: {
        uuid: consumer_uuid,
      },
      body: {
        reward_uuid: prize_uuid,
        qty,
        ...(address && {
          address: convertFormAddress(address),
        }),
      },
    });

    if (!data?.data) {
      throw new Error("No data found");
    }

    return {
      ...data.data,
      statusCode: response.status,
      message: "",
    };
  }

  async getEarningRulePoints(event_name: string, _: string, user_uuid: string): Promise<number> {
    try {
      const { data } = await getEarningRuleByName({
        client: this.client,
        path: {
          event_name,
          uuid: user_uuid,
        },
      });

      if (!data?.data) {
        return 0;
      }

      return data.data.points;
    } catch {
      return 0;
    }
  }

  async getQrCodeDetails({ uuid = "", qrCodeId }: GetQrCodeDetailsPayload): Promise<GetQrCodeDetailsResponseType> {
    const { data } = await getQrCodeDetails({
      client: this.client,
      path: {
        id: qrCodeId,
        uuid,
      },
    });

    if (!data?.data) {
      throw new Error("No data found");
    }
    return data.data;
  }

  async readArticleAction(article_id: string, _: string, uuid: string = ""): Promise<ArticleReadActionReturnType> {
    const { response } = await updateArticleRead({
      client: this.client,
      body: {
        article_id,
      },
      path: {
        uuid,
      },
    });
    return { statusCode: response.status };
  }

  async watchVideoAction(uuid: string, video_id: string): Promise<VideoWatchActionReturnType> {
    const { response } = await updateVideoWatched({
      client: this.client,
      body: {
        video_id,
      },
      path: {
        uuid,
      },
    });
    return { statusCode: response.status };
  }

  async scanQrAction(qr_code_id: string, _: string, uuid: string | undefined = ""): Promise<ScanQrActionReturnType> {
    const { data, response } = await getQrCodeRead({
      client: this.client,
      path: {
        uuid,
      },
      body: {
        qr_code_id,
      },
    });

    if (!data?.data) {
      throw new Error("No data found");
    }

    return {
      ...data.data,
      statusCode: response.status,
    } as ScanQrActionReturnType;
  }

  async getContestStatus(uuid: string): Promise<GetContestsResponseType> {
    const { data } = await getContestStatus({
      client: this.client,
      path: {
        uuid,
      },
    });
    if (!data?.data) {
      throw new Error("No Contest data found");
    }
    return data.data;
  }

  async postContest(uuid: string, _: string, contestSubmitData: CreateContestRequestBody): Promise<void> {
    await createContest({
      client: this.client,
      body: contestSubmitData,
      headers: {
        "Content-Type": null,
      },
      path: {
        uuid,
      },
      bodySerializer: contestsFormDataBodySerializer,
    });
  }

  async getPrizes({
    orderBy,
    page,
    perPage,
    sort,
    is_virtual,
    location_ids,
    consumer_uuid,
    type,
    rewardType,
    is_lottery,
    available_for_me,
    min_points,
    max_points,
    levels,
    subcategories,
  }: GetPrizesInputType): Promise<RestResponseType<GetPrizesResponseType>> {
    const query = {
      page,
      order_by: orderBy,
      is_virtual,
      location_ids,
      per_page: perPage,
      sort,
      prize_type: convertRewardFilterToEvoRewardType(type),
      type: rewardType,
      is_lottery,
      available_for_me,
      min_points,
      max_points,
      levels: levels?.join(", "),
      subcategories: subcategories?.join(", "),
    };

    const request = consumer_uuid
      ? await listRewardsExtended({
          client: this.client,
          path: {
            uuid: consumer_uuid,
          },
          query,
        })
      : await listRewards({
          client: this.client,
          query,
        });

    const { data, response } = request;

    if (!data?.data) {
      throw new Error("No data found");
    }

    return {
      ...convertEvoGetPrizes(data),
      statusCode: response.status,
    };
  }

  async getRewardFilters(): Promise<GetRewardFiltersResponseType> {
    const { data } = await listRewardFilters({
      client: this.client,
    });

    if (!data?.data) {
      throw new Error("No data found in getRewardFilters");
    }

    return convertEvoRewardFilters(data);
  }

  async getProductPoints(
    sku = "",
    quantity = 1,
    uuid: string | null = null,
    ciam_token: string | null = null
  ): Promise<GetProductPointsReturnType> {
    const query = { "identifier[]": [sku], type: "product" as const };

    const { data } =
      uuid && ciam_token
        ? await getEarningsForConsumer({
            client: this.client,
            path: { uuid },
            query,
          })
        : await listEarnings({ client: this.client, query });

    if (!data?.data) throw new Error("No data found in getProductPoints");

    const product = data.data.find((item) => item.identifier === sku);

    if (!product) {
      return {
        points: 0,
        rules: [],
        time_limited: false,
      };
    }

    const rules = [
      {
        multiply: true, // EVO does not support multiply points for products as it does not support QTY
        points: product.points_without_multiply * quantity + product.points_with_multiply,
      },
    ];

    return {
      points: calculateProductPoints(rules, quantity),
      rules,
      time_limited: product.validity_rules?.time?.limited ?? false,
    };
  }

  async getReferralHistory(
    consumer_uuid: string,
    _ciam_token: string,
    perPage = 5,
    page = 1
  ): Promise<CustomerReferralHistoryResponseType> {
    const { data } = await getConsumerPointsHistory({
      client: this.client,
      path: { uuid: consumer_uuid },
      query: {
        type: "referral",
        page,
        per_page: perPage,
      },
    });

    if (!data?.data) {
      throw new Error("No data found in getReferralHistory");
    }

    return convertReferralHistoryEvoResponse(data);
  }

  async getReward({ reward_uuid }: GetRewardPayload): Promise<GetRewardResponse> {
    const { data } = await getReward({
      client: this.client,
      path: { reward_uuid },
    });

    if (!data?.data) {
      throw new Error("No data found in getReward");
    }

    return data;
  }

  async getRewardExtended({
    consumer_uuid,
    reward_uuid,
  }: GetRewardExtendedPayload): Promise<GetRewardExtendedResponse> {
    const { data } = await getRewardExtended({
      client: this.client,
      path: {
        uuid: consumer_uuid,
        reward_uuid,
      },
    });

    if (!data?.data) {
      throw new Error("No data found in getRewardExtended");
    }

    return data;
  }

  async getAchievements(consumer_uuid: string): Promise<GetAchievementsResponse> {
    const { data } = await getAchievements({
      client: this.client,
      path: { uuid: consumer_uuid },
    });

    if (!data?.data) {
      throw new Error("No data found in getAchievements");
    }

    return data.data;
  }

  async completeQuizAction(quiz_id: string, uuid: string = ""): Promise<QuizCompleteActionReturnType> {
    const { response } = await updateQuizCompleted({
      client: this.client,
      body: { quiz_id },
      path: { uuid },
    });
    return { statusCode: response.status };
  }
}
